/**
 * Pricing Setup Component
 *
 * Simplified pricing rules management with unified rule structure
 */

import React, { useState, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { Button, Form, Modal, Table, Tag, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { PricingRuleForm } from './helper/form';
import { IPricingRule, IPricingRuleForm, IPricingOptionReference } from '../../types/pricingSetup';
import type { ColumnsType } from 'antd/es/table';
import { useGetProductConfigurationJson, usePutConfigLive, usePostConfigUploadOverwrite } from '../../hooks';
import { useAllConfigOptions } from '../../hooks/configOption';
import { useCategoryList } from '../../hooks/categorySetup';
import { useProductList } from '../../hooks/productSetup';
import { IProduct } from '../../types/productSetup';
import { IChatBotLiveConfig } from '../../services';
import { incrementVersion } from '@/utils/version';
import { useQueryClient } from '@tanstack/react-query';
import { queryKey } from '../../constants/queryKey';

export const PricingSetup: React.FC = () => {
  const { accounts } = useMsal();
  const currentUserEmail = accounts[0]?.username ?? '';
  const queryClient = useQueryClient();

  const [form] = Form.useForm<IPricingRuleForm>();
  const [showEditor, setShowEditor] = useState(false);
  const [editingPricingRule, setEditingPricingRule] = useState<IPricingRule | null>(null);

  // Backend configuration
  const { data: backendConfigData, isLoading: backendConfigLoading } = useGetProductConfigurationJson();

  // Save mutation
  const { mutate: putConfigLive, isPending: isSaving } = usePutConfigLive();
  const { mutate: uploadOverwrite } = usePostConfigUploadOverwrite();

  // Fetch all configuration options
  const { data: allConfigOptionsData } = useAllConfigOptions();
  const allConfigOptions = allConfigOptionsData || [];

  // Fetch categories
  const { data: categoriesData } = useCategoryList();
  const categories = categoriesData?.value || [];

  // Fetch active products for Variation trigger options
  const { data: productsData } = useProductList({ queryParams: { current: 1, pageSize: 1000 } });
  const allProducts: IProduct[] = (productsData?.value || []).filter(p => p.statecode === 0);

  // Category options for select
  const categoryOptions = useMemo(() => {
    return categories.map(cat => ({
      value: cat.dogma_categoryid,
      label: cat.dogma_name,
      categoryType: cat.dogma_categorytype,
    }));
  }, [categories]);

  const pricingRulesList: IPricingRule[] = backendConfigData?.Config?.pricingRules ?? [];

  // Helper to get option name by ID (checks config options then products)
  const getOptionName = (optionId: string): string => {
    const option = allConfigOptions.find(opt => opt.dogma_configurationoptionsid === optionId);
    if (option) return option.dogma_name;
    const product = allProducts.find(p => p.productid === optionId);
    return product?.name || optionId;
  };

  // Helper to convert option IDs to IPricingOptionReference[]
  const convertOptionIdsToReferences = (optionIds: string[], fallbackCategoryId?: string): IPricingOptionReference[] => {
    return optionIds.flatMap(optionId => {
      const option = allConfigOptions.find(opt => opt.dogma_configurationoptionsid === optionId);
      if (option) {
        return [{
          categoryId: option._dogma_category_value || fallbackCategoryId || '',
          optionId,
          optionName: option.dogma_name || '',
          optionType: option.dogma_optiontype || 0,
          optionTypeName: option[`dogma_optiontype@OData.Community.Display.V1.FormattedValue`] || '',
        }];
      }
      // Fall back to products (used when Variation trigger includes product items)
      const product = allProducts.find(p => p.productid === optionId);
      if (product) {
        return [{
          categoryId: fallbackCategoryId || '',
          optionId,
          optionName: product.name || '',
          optionType: 64,
          optionTypeName: 'Variation',
        }];
      }
      // Skip entirely only if the ID is not found anywhere
      return [];
    });
  };

  // Migrate a legacy rule (flat arrays) to the new groups format for editing/display
  const migrateRuleToGroups = (rule: IPricingRule): IPricingRule => {
    if (rule.dogma_trigger_groups) return rule;
    const migrated = { ...rule };
    if (rule.dogma_trigger_optionids?.length) {
      const byCategory = new Map<string, IPricingOptionReference[]>();
      for (const opt of rule.dogma_trigger_optionids) {
        if (!byCategory.has(opt.categoryId)) byCategory.set(opt.categoryId, []);
        byCategory.get(opt.categoryId)!.push(opt);
      }
      migrated.dogma_trigger_groups = Array.from(byCategory.values());
    } else {
      migrated.dogma_trigger_groups = [];
    }
    if (rule.dogma_expected_optionids?.length) {
      migrated.dogma_expected_groups = [rule.dogma_expected_optionids];
    }
    return migrated;
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      // Convert group arrays of option IDs → IPricingOptionReference[][]
      const triggerGroups: IPricingOptionReference[][] = (values.dogma_trigger_groups ?? []).map(
        (groupIds: string[]) => convertOptionIdsToReferences(groupIds)
      );
      const expectedGroups: IPricingOptionReference[][] = (values.dogma_expected_groups ?? [])
        .filter((g: string[]) => g?.length)
        .map((groupIds: string[]) => convertOptionIdsToReferences(groupIds));

      let updatedPricingRulesList: IPricingRule[];

      if (editingPricingRule) {
        updatedPricingRulesList = pricingRulesList.map(rule =>
          rule.dogma_pricingruleid === editingPricingRule.dogma_pricingruleid
            ? {
                dogma_pricingruleid: rule.dogma_pricingruleid,
                dogma_name: values.dogma_name,
                resolverValue: rule.resolverValue ?? rule.dogma_name
                  .toLowerCase()
                  .trim()
                  .replace(/[^a-z0-9]+/g, '_')
                  .replace(/^_+|_+$/g, ''),
                dogma_trigger_groups: triggerGroups,
                dogma_condition_operator: values.dogma_condition_operator,
                dogma_condition_value: values.dogma_condition_value,
                dogma_expected_groups: expectedGroups.length ? expectedGroups : undefined,
                dogma_operation: 'add',
                dogma_price_value: values.dogma_price_value,
                dogma_apply_per_unit: values.dogma_apply_per_unit ?? false,
                dogma_apply_per_sash: values.dogma_apply_per_sash ?? false,
                statecode: rule.statecode,
                createdon: rule.createdon,
                modifiedon: new Date().toISOString(),
              }
            : rule
        );
      } else {
        const resolverValue = values.dogma_name
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '');
        const newPricingRule: IPricingRule = {
          dogma_pricingruleid: `pricing-rule-${Date.now()}`,
          dogma_name: values.dogma_name,
          resolverValue,
          dogma_trigger_groups: triggerGroups,
          dogma_condition_operator: values.dogma_condition_operator,
          dogma_condition_value: values.dogma_condition_value,
          dogma_expected_groups: expectedGroups.length ? expectedGroups : undefined,
          dogma_operation: 'add',
          dogma_price_value: values.dogma_price_value,
          dogma_apply_per_unit: values.dogma_apply_per_unit ?? false,
          dogma_apply_per_sash: values.dogma_apply_per_sash ?? false,
          statecode: 0,
          createdon: new Date().toISOString(),
        };
        updatedPricingRulesList = [...pricingRulesList, newPricingRule];
      }

      // Save to backend configuration
      const payload: IChatBotLiveConfig = {
        Config: {
          ...(backendConfigData?.Config ?? {} as any),
          pricingRules: updatedPricingRulesList,
          lastUpdated: new Date().toISOString(),
          lastUpdatedBy: currentUserEmail,
        },
        Version: incrementVersion(backendConfigData?.Version)
      };

      putConfigLive(payload, {
        onSuccess: () => {
          // The hook fires invalidateQueries immediately after putConfigLive,
          // which triggers a refetch of api/config/latest. But the blob there
          // is only updated by uploadOverwrite (called below), so the refetch
          // would return stale data and overwrite our changes.
          // Cancel that premature refetch, set the data optimistically, then
          // re-invalidate after uploadOverwrite finishes.
          queryClient.cancelQueries({ queryKey: [queryKey.getConfigurationJsonContent] });
          queryClient.setQueryData([queryKey.getConfigurationJsonContent], payload);

          uploadOverwrite({ body: payload, version: payload.Version }, {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: [queryKey.getConfigurationJsonContent] });
            },
          });

          setShowEditor(false);
          form.resetFields();
          setEditingPricingRule(null);
        },
      });
    } catch (error) {
    }
  };

  const handleEdit = (pricingRule: IPricingRule) => {
    setEditingPricingRule(pricingRule);
    const migrated = migrateRuleToGroups(pricingRule);

    const triggerGroupIds = (migrated.dogma_trigger_groups ?? []).map(g => g.map(r => r.optionId));
    const expectedGroupIds = (migrated.dogma_expected_groups ?? []).map(g => g.map(r => r.optionId));

    form.setFieldsValue({
      dogma_name: migrated.dogma_name,
      dogma_trigger_groups: triggerGroupIds,
      dogma_condition_operator: migrated.dogma_condition_operator,
      dogma_condition_value: migrated.dogma_condition_value,
      dogma_expected_groups: expectedGroupIds,
      dogma_operation: migrated.dogma_operation,
      dogma_price_value: migrated.dogma_price_value,
      dogma_apply_per_unit: migrated.dogma_apply_per_unit ?? false,
      dogma_apply_per_sash: migrated.dogma_apply_per_sash ?? false,
    });

    setShowEditor(true);
  };

  const handleDelete = async (pricingRuleId: string) => {
    const updatedPricingRulesList = pricingRulesList.filter(
      rule => rule.dogma_pricingruleid !== pricingRuleId
    );

    // Save to backend configuration
    const payload: IChatBotLiveConfig = {
      Config: {
        ...(backendConfigData?.Config ?? {} as any),
        pricingRules: updatedPricingRulesList,
        lastUpdated: new Date().toISOString(),
        lastUpdatedBy: currentUserEmail,
      },
      Version: incrementVersion(backendConfigData?.Version)
    };

    putConfigLive(payload, {
      onSuccess: () => {
        uploadOverwrite({ body: payload, version: payload.Version });
      },
    });
  };

  const handleCancel = () => {
    setShowEditor(false);
    form.resetFields();
    setEditingPricingRule(null);
  };

  const handleAddNew = () => {
    setEditingPricingRule(null);
    form.resetFields();
    setShowEditor(true);
  };

  // Render groups as "(A or B) AND (C or D)"
  const renderGroups = (groups: IPricingOptionReference[][]): React.ReactNode => (
    <span>
      {groups.map((group, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-orange-500 font-bold mx-1">AND</span>}
          <span>(</span>
          {group.map((ref, j) => (
            <React.Fragment key={j}>
              {j > 0 && <span className="text-blue-400 mx-1">or</span>}
              <span>{getOptionName(ref.optionId)}</span>
            </React.Fragment>
          ))}
          <span>)</span>
        </React.Fragment>
      ))}
    </span>
  );

  // Render rule details in table
  const renderRuleDetails = (rule: IPricingRule) => {
    const migrated = migrateRuleToGroups(rule);
    const triggerGroups = migrated.dogma_trigger_groups ?? [];
    const expectedGroups = migrated.dogma_expected_groups ?? [];

    const operationLabels = { add: 'Add', multiply: 'Multiply' };

    return (
      <div className="text-sm space-y-1">
        {/* Trigger */}
        <div className="font-mono">
          <span className="text-blue-600 font-semibold">TRIGGER:</span>{' '}
          {triggerGroups.length > 0 ? renderGroups(triggerGroups) : <span className="text-gray-400">—</span>}
        </div>

        {/* Condition */}
        {rule.dogma_condition_operator && (
          <div className="font-mono text-orange-600">
            <span className="font-semibold">IF:</span> {rule.dogma_condition_operator}{' '}
            {rule.dogma_condition_operator !== 'exists' && rule.dogma_condition_value}
          </div>
        )}

        {/* Expected */}
        {expectedGroups.length > 0 && (
          <div className="font-mono text-purple-600">
            <span className="font-semibold">APPLY TO:</span>{' '}
            {renderGroups(expectedGroups)}
          </div>
        )}

        {/* Action */}
        <div className="font-mono text-green-700">
          <span className="font-semibold">ACTION:</span> {operationLabels[rule.dogma_operation ?? 'add']}{' '}
          £{rule.dogma_price_value}
          {rule.dogma_apply_per_unit && (
            <Tag color="cyan" className="ml-2" style={{ fontSize: 11 }}>per frame</Tag>
          )}
          {rule.dogma_apply_per_sash && (
            <Tag color="purple" className="ml-2" style={{ fontSize: 11 }}>per sash</Tag>
          )}
        </div>
      </div>
    );
  };

  const columns: ColumnsType<IPricingRule> = [
    {
      title: 'Rule Name',
      dataIndex: 'dogma_name',
      key: 'dogma_name',
      width: 200,
      render: (text: string) => (
        <div className="font-medium">{text}</div>
      ),
    },
    {
      title: 'Operation',
      dataIndex: 'dogma_operation',
      key: 'dogma_operation',
      width: 120,
      render: (operation: string) => {
        const operationColors = {
          add: 'green',
          multiply: 'blue',
        };
        const op = operation ?? 'add';
        return (
          <Tag color={operationColors[op as keyof typeof operationColors]}>
            {op.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Rule Details',
      key: 'details',
      render: (_, record: IPricingRule) => renderRuleDetails(record),
    },
    {
      title: 'Status',
      dataIndex: 'statecode',
      key: 'statecode',
      width: 100,
      render: (statecode: number) => (
        <Tag color={statecode === 0 ? 'green' : 'default'}>
          {statecode === 0 ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            className='text-primary'
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Popconfirm
            title="Delete Pricing Rule"
            description="Are you sure you want to delete this pricing rule?"
            onConfirm={() => handleDelete(record.dogma_pricingruleid)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className='bg-white rounded-lg'>
        <div className="flex h-full items-center justify-between px-4 py-4 border-b border-neutral-400 gap-x-4 border-0">
          <div>
            <h1 className='text-lg font-semibold text-text-primary'>Pricing Rules</h1>
            <p className='text-sm text-text-secondary'>
              Simplified unified pricing rules - configure triggers, conditions, and actions in one place
            </p>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddNew}
          >
            Add Pricing Rule
          </Button>
        </div>

        <div className="p-4">
          <Table
            columns={columns}
            dataSource={pricingRulesList}
            rowKey="dogma_pricingruleid"
            loading={backendConfigLoading}
            scroll={{ x: 1400 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} rules`,
            }}
          />
        </div>
      </div>

      <Modal
        title={editingPricingRule ? 'Edit Pricing Rule' : 'Add Pricing Rule'}
        open={showEditor}
        onCancel={handleCancel}
        onOk={handleSave}
        okText="Save"
        cancelText="Cancel"
        width={800}
        confirmLoading={isSaving}
        styles={{
          body: {
            maxHeight: '70vh',
            overflowY: 'auto',
          },
        }}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <PricingRuleForm
            categories={categoryOptions}
            allConfigOptions={allConfigOptions}
            products={allProducts}
          />
        </Form>
      </Modal>
    </>
  );
};
